import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export type SortOption =
  | 'new-old'
  | 'last-user'
  | 'author-posts'
  | 'author-replies'
  | 'author-replies-count'
  | 'event-users';
export type ReplyRangeDays = 7 | 15 | 30 | 60;

interface ConversationTabBarProps {
  activeSort: SortOption;
  onSortChange: (sort: SortOption) => void;
  onSearchClick?: () => void;  // Navigate to search page
  replyRangeDays?: ReplyRangeDays;
  onReplyRangeChange?: (range: ReplyRangeDays) => void;
}

const tabs = [
  { key: 'new-old', label: 'New → Old' },
  { key: 'author-posts', label: 'Shares New → Old' },
  { key: 'author-replies', label: 'Replies New → Old' },
  { key: 'author-replies-count', label: 'Replies Most → Least' },
  { key: 'event-users', label: 'Event users' },
  { key: 'last-user', label: 'Last User Replies' },
] as const;

const replyRanges: ReplyRangeDays[] = [7, 15, 30, 60];

function SearchButton({ onClick }: { onClick: () => void }) {
  return (
    <motion.button
      onClick={onClick}
      className="w-10 h-10 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-md hover:bg-white/80 dark:hover:bg-gray-800/80 transition-all duration-200 ease-out group flex items-center justify-center shadow-sm"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
    >
      <svg
        className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-colors duration-200"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    </motion.button>
  );
}

function ToggleBar({
  activeSort,
  onSortChange,
}: {
  activeSort: SortOption;
  onSortChange: (sort: SortOption) => void;
}) {
  const [selectorStyle, setSelectorStyle] = useState<{ left: string; width: string }>({ left: '0%', width: '20%' });
  
  useEffect(() => {
    const idx = tabs.findIndex((t) => t.key === activeSort);
    const w = 100 / tabs.length;
    setSelectorStyle({ left: `${idx * w}%`, width: `${w}%` });
  }, [activeSort]);

  return (
    <motion.div
      className="flex-1 relative bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-1 shadow-inner"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="absolute top-1 bottom-1 bg-white dark:bg-gray-700 rounded-xl shadow-lg shadow-black/10 border border-gray-200/50 dark:border-gray-600/50"
        style={{ left: selectorStyle.left, width: selectorStyle.width, transform: 'translateX(1px)' }}
        animate={{ left: selectorStyle.left, width: selectorStyle.width }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      />
      <div className="relative flex">
        {tabs.map((tab, i) => {
          const isActive = activeSort === tab.key;
          return (
            <motion.button
              key={tab.key}
              onClick={() => onSortChange(tab.key)}
              className="flex-1 relative h-10 px-3 flex items-center justify-center transition-all duration-200 ease-out"
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05, duration: 0.2 }}
            >
              <span
                className={`text-sm tracking-[-0.2px] whitespace-nowrap transition-all duration-300 ${
                  isActive
                    ? "font-['SF_Pro_Display:Semibold',_sans-serif] text-gray-900 dark:text-gray-100"
                    : "font-['SF_Pro_Display:Medium',_sans-serif] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                }`}
              >
                {tab.label}
              </span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

export function ConversationTabBar({
  activeSort,
  onSortChange,
  onSearchClick = () => {},
  replyRangeDays,
  onReplyRangeChange,
}: ConversationTabBarProps) {
  return (
    <div className="w-full max-w-4xl mx-auto">
      <motion.div className="flex items-center gap-4" layout transition={{ type: 'spring', stiffness: 300, damping: 30 }}>
        <ToggleBar activeSort={activeSort} onSortChange={onSortChange} />
        {activeSort === 'last-user' && replyRangeDays && onReplyRangeChange && (
          <div className="flex items-center bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl p-1 shadow-inner">
            {replyRanges.map((range) => {
              const isActive = replyRangeDays === range;
              return (
                <button
                  key={range}
                  type="button"
                  onClick={() => onReplyRangeChange(range)}
                  className={`px-3 h-10 rounded-xl text-sm transition-all duration-200 ${
                    isActive
                      ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100 font-['SF_Pro_Display:Semibold',_sans-serif]"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  }`}
                >
                  {range}d
                </button>
              );
            })}
          </div>
        )}
        <SearchButton onClick={onSearchClick} />
      </motion.div>
    </div>
  );
}
